import { useCallback, useEffect } from 'react';
import clsx from 'clsx';
import { useUiStore } from '@store/uiStore';
import {
  FullscreenExitIcon,
  FullscreenIcon,
  MenuIcon,
  NvrLogoMark,
  ProfileIcon,
  SettingsIcon,
} from './icons';

interface AppHeaderProps {
  sectionTitle: string;
  productName?: string;
  companyName?: string;
  onSettingsClick?: () => void;
  onProfileClick?: () => void;
}

const HEADER_ICON_SIZE = 32;

function HeaderIconButton({
  label,
  active = false,
  onClick,
  children,
}: {
  label: string;
  active?: boolean;
  onClick?: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      aria-pressed={onClick ? active : undefined}
      onClick={onClick}
      className={clsx(
        'inline-flex items-center justify-center rounded-md transition-colors',
        'hover:bg-[var(--app-hover-soft)] focus-visible:outline-none focus-visible:ring-2',
      )}
      style={{
        width: HEADER_ICON_SIZE,
        height: HEADER_ICON_SIZE,
        color: 'var(--app-text-primary)',
      }}
    >
      {children}
    </button>
  );
}

/**
 * Global app header per Jira OOM-1256.
 *
 * Layout: 64px tall, light surface, three regions
 *   - Left: section title (reflects sidebar selection)
 *   - Center: product/company branding
 *   - Right: settings + fullscreen toggle + profile
 *
 * The component is layout-only. Real navigation state (selected section) is
 * passed in by `AppShell`, which derives it from the active route.
 */
export function AppHeader({
  sectionTitle,
  productName = 'Smart NVR',
  companyName = 'AllCAD',
  onSettingsClick,
  onProfileClick,
}: AppHeaderProps): JSX.Element {
  const { sidebarCollapsed, toggleSidebar, isFullscreen, setFullscreen } = useUiStore();

  const handleFullscreen = useCallback(async (): Promise<void> => {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
      } else if (document.exitFullscreen) {
        await document.exitFullscreen();
      }
    } catch {
      // Browser may reject (permission, iframe sandbox); silently ignore.
    }
  }, []);

  useEffect(() => {
    const sync = (): void => setFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', sync);
    return () => document.removeEventListener('fullscreenchange', sync);
  }, [setFullscreen]);

  return (
    <header
      className="flex items-center justify-between gap-4 px-8"
      style={{
        height: 'var(--app-header-h)',
        backgroundColor: 'var(--app-header-bg)',
        borderBottom: '1px solid var(--app-border)',
        color: 'var(--app-text-primary)',
      }}
    >
      <div className="flex items-center gap-3">
        <HeaderIconButton
          label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          active={!sidebarCollapsed}
          onClick={toggleSidebar}
        >
          <MenuIcon />
        </HeaderIconButton>
        <h1 className="truncate text-base font-medium" title={sectionTitle}>
          {sectionTitle}
        </h1>
      </div>

      <div className="flex items-center gap-3">
        <NvrLogoMark size={28} style={{ color: 'var(--app-primary)' }} />
        <div className="flex flex-col leading-tight">
          <span className="text-base font-semibold">{productName}</span>
          <span className="text-xs" style={{ color: 'var(--app-accent)' }}>
            {companyName}
          </span>
        </div>
      </div>

      <div className="flex items-center gap-2">
        {onSettingsClick && (
          <HeaderIconButton label="Settings" onClick={onSettingsClick}>
            <SettingsIcon />
          </HeaderIconButton>
        )}
        <HeaderIconButton
          label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
          active={isFullscreen}
          onClick={() => {
            void handleFullscreen();
          }}
        >
          {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
        </HeaderIconButton>
        <HeaderIconButton label="Profile" onClick={onProfileClick}>
          <ProfileIcon />
        </HeaderIconButton>
      </div>
    </header>
  );
}
